import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { saveBlob } from '@/lib/save-blob';
import { readMedia } from '@/lib/media';
import {
  photoResource,
  type DeletePhotosBody,
  type PhotoRef,
  type UpdatePhotoBody,
  type UpdatePhotosBody,
} from './photo.resource';

export const photosQuery = (organizationId: string, albumId?: string) =>
  queryOptions({
    queryKey: ['organizations', organizationId, 'photos', albumId ?? 'all'],
    queryFn: () => photoResource.list(organizationId, albumId),
  });

export function usePhotos(organizationId: string | null, albumId?: string) {
  return useQuery({
    ...photosQuery(organizationId ?? '', albumId),
    enabled: Boolean(organizationId),
    select: (data) => data.photos,
  });
}

function useRefresh() {
  const client = useQueryClient();
  return async (organizationId: string) => {
    await client.invalidateQueries({
      queryKey: ['organizations', organizationId, 'photos'],
    });
    await client.invalidateQueries({
      queryKey: ['organizations', organizationId, 'albums'],
    });
  };
}

export interface AddPhotoInput {
  organizationId: string;
  file: File;
  albumId?: string;
}

export function useAddPhoto() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async ({ organizationId, file, albumId }: AddPhotoInput) => {
      const meta = await readMedia(file);
      return photoResource.add({
        organizationId,
        file,
        albumId,
        thumbnail: meta.thumbnail,
        takenAt: meta.takenAt.toISOString(),
        width: meta.width,
        height: meta.height,
        durationMs: meta.durationMs,
      });
    },
    onSuccess: (_data, input) => refresh(input.organizationId),
  });
}

export function useUpdatePhoto() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (body: UpdatePhotoBody) => photoResource.update(body),
    onSuccess: (_data, body) => refresh(body.organizationId),
  });
}

export function useUpdatePhotos() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (body: UpdatePhotosBody) => photoResource.updateMany(body),
    onSuccess: (_data, body) => refresh(body.organizationId),
  });
}

export function useDeletePhoto() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (ref: PhotoRef) => photoResource.remove(ref),
    onSuccess: (_data, ref) => refresh(ref.organizationId),
  });
}

export function useDeletePhotos() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (body: DeletePhotosBody) => photoResource.removeMany(body),
    onSuccess: (_data, body) => refresh(body.organizationId),
  });
}

export function useDownloadPhotos() {
  return useMutation({
    mutationFn: async ({
      organizationId,
      photos,
    }: {
      organizationId: string;
      photos: Array<{ id: string; filename: string }>;
    }) => {
      for (const photo of photos) {
        const blob = await photoResource.get({ organizationId, photoId: photo.id });
        saveBlob(blob, photo.filename);
      }
    },
  });
}

export const photoLoader =
  (organizationId: string) =>
  (photo: { id: string }): Promise<Blob> =>
    photoResource.get({ organizationId, photoId: photo.id });

export const photoThumbnailLoader =
  (organizationId: string) =>
  (photo: { id: string }): Promise<Blob> =>
    photoResource.getThumbnail({ organizationId, photoId: photo.id });
